import { Deferred, Head, router, usePage } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import { AnimatePresence, motion } from "framer-motion";
import {
	Check,
	ChevronsUpDown,
	Copy,
	EditIcon,
	ListFilterIcon,
	MoreHorizontal,
	PlusIcon,
	Trash2,
} from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { MitraBlastConfigForm } from "@/components/admin-portal/mitra-blast-config/form";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { getCsrfToken } from "@/lib/csrf";
import { groupLocationsByCountry } from "@/lib/locations";
import {
	cn,
	copyToClipboard,
	formatDateTime,
	populateQueryParams,
} from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import type { MitraBlastConfig } from "@/types/admin-portal/mitra-blast-config";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export interface PageProps {
	configs: {
		data: MitraBlastConfig[];
		metadata: Metadata;
	};
	locations: Location[];
	formOptions: {
		locations: Location[];
		services: {
			id: number;
			name: string;
		}[];
		locationServices: {
			locationId: number;
			serviceId: number;
			serviceName: string;
		}[];
	};
}

export interface MitraBlastConfigIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: unknown;
}

export default function Index({ configs, locations, formOptions }: PageProps) {
	const { props: globalProps } =
		usePage<MitraBlastConfigIndexGlobalPageProps>();
	const { t, i18n } = useTranslation("mitra-blast-configs");
	const { toast } = useToast();
	const currentLocale = i18n.language;
	const configsIndexRoute = useMemo(
		() =>
			globalProps.adminPortal?.router?.adminPortal?.mitraBlastConfigs?.index,
		[globalProps.adminPortal?.router?.adminPortal?.mitraBlastConfigs?.index],
	);
	const currentCity = useMemo(
		() => globalProps.adminPortal?.currentQuery?.city || "",
		[globalProps.adminPortal?.currentQuery?.city],
	);
	const currentStatus = useMemo(
		() => globalProps.adminPortal?.currentQuery?.status || "",
		[globalProps.adminPortal?.currentQuery?.status],
	);
	const currentService = useMemo(
		() => globalProps.adminPortal?.currentQuery?.service || "",
		[globalProps.adminPortal?.currentQuery?.service],
	);

	const configsData = useMemo(
		() => (Array.isArray(configs?.data) ? configs.data : []),
		[configs?.data],
	);

	const groupedLocations = useMemo(() => {
		if (locations && locations.length > 0) {
			return groupLocationsByCountry(locations);
		}
		return [];
	}, [locations]);

	const [isShowFilter, setIsShowFilter] = useState(false);
	const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
	const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
	const [servicePopoverOpen, setServicePopoverOpen] = useState(false);
	const [statusDialogOpen, setStatusDialogOpen] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [selectedConfig, setSelectedConfig] = useState<MitraBlastConfig | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [pendingDeleteConfig, setPendingDeleteConfig] =
		useState<MitraBlastConfig | null>(null);
	const [pendingStatusChange, setPendingStatusChange] = useState<{
		config: MitraBlastConfig;
		newStatus: boolean;
	} | null>(null);
	const handleToggleFilter = useCallback(() => {
		setIsShowFilter((prev) => !prev);
	}, []);

	const handleCityChange = useCallback(
		(city: string) => {
			if (!configsIndexRoute) return;

			const { fullUrl, queryParams } = populateQueryParams(configsIndexRoute, {
				city: city === "all" ? null : city,
			});

			router.get(fullUrl, queryParams, {
				only: ["configs", "adminPortal", "flash", "errors"],
				preserveScroll: true,
				preserveState: true,
			});
		},
		[configsIndexRoute],
	);

	const handleStatusChange = useCallback(
		(status: string) => {
			if (!configsIndexRoute) return;

			const { fullUrl, queryParams } = populateQueryParams(configsIndexRoute, {
				status: status === "all" ? null : status,
			});

			router.get(fullUrl, queryParams, {
				only: ["configs", "adminPortal", "flash", "errors"],
				preserveScroll: true,
				preserveState: true,
			});
		},
		[configsIndexRoute],
	);

	const handleServiceChange = useCallback(
		(serviceId: string) => {
			if (!configsIndexRoute) return;

			const { fullUrl, queryParams } = populateQueryParams(configsIndexRoute, {
				service: serviceId === "all" ? null : serviceId,
			});

			router.get(fullUrl, queryParams, {
				only: ["configs", "adminPortal", "flash", "errors"],
				preserveScroll: true,
				preserveState: true,
			});
		},
		[configsIndexRoute],
	);

	const handleClearAll = useCallback(() => {
		if (!configsIndexRoute) return;

		const { fullUrl, queryParams } = populateQueryParams(configsIndexRoute, {
			city: null,
			status: null,
			service: null,
		});

		router.get(fullUrl, queryParams, {
			only: ["configs", "adminPortal", "flash", "errors"],
			preserveScroll: true,
			preserveState: true,
		});
	}, [configsIndexRoute]);

	const changePage = useCallback(
		(
			type: "prev" | "next" | "first" | "last" | "limit" | "page",
			value?: string,
		) => {
			if (!configs?.metadata) return;

			let url: string | undefined;
			switch (type) {
				case "prev":
					url = configs?.metadata.prevUrl;
					break;
				case "next":
					url = configs?.metadata.nextUrl;
					break;
				case "first":
					url = configs?.metadata.firstUrl;
					break;
				case "last":
					url = configs?.metadata.lastUrl;
					break;
				case "limit": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(configs?.metadata.pageUrl, {
						limit: value,
					});
					url = fullUrl;
					break;
				}
				case "page": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(configs?.metadata.pageUrl, {
						page: value,
					});
					url = fullUrl;
					break;
				}
			}

			if (!url) return;

			router.get(
				url,
				{},
				{
					preserveScroll: false,
					preserveState: true,
					only: ["configs", "adminPortal", "flash", "errors"],
				},
			);
		},
		[configs?.metadata],
	);

	const handleEdit = useCallback((config: MitraBlastConfig) => {
		setSelectedConfig(config);
		setEditDialogOpen(true);
	}, []);

	const handleDelete = useCallback((config: MitraBlastConfig) => {
		setPendingDeleteConfig(config);
		setDeleteDialogOpen(true);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		if (!pendingDeleteConfig) return;

		try {
			const response = await fetch(
				`/admin-portal/mitra-blast-configs/${pendingDeleteConfig.id}`,
				{
					method: "DELETE",
					headers: {
						"Content-Type": "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
				},
			);

			const data = await response.json();

			if (data.success) {
				toast({
					title: t("toast.delete_success"),
					description: data.message,
				});

				router.reload({
					only: ["configs", "adminPortal", "flash", "errors", "formOptions"],
				});
			} else {
				throw new Error(data.message || "Failed to delete config");
			}
		} catch (error) {
			console.error("Failed to delete config:", error);
			toast({
				title: t("toast.delete_error"),
				description:
					error instanceof Error ? error.message : t("toast.please_try_again"),
				variant: "destructive",
			});
		} finally {
			setDeleteDialogOpen(false);
			setPendingDeleteConfig(null);
		}
	}, [pendingDeleteConfig, toast, t]);

	const handleDeleteCancel = useCallback(() => {
		setDeleteDialogOpen(false);
		setPendingDeleteConfig(null);
	}, []);

	const handleStatusToggle = useCallback(
		(config: MitraBlastConfig, newStatus: boolean) => {
			setPendingStatusChange({ config, newStatus });
			setStatusDialogOpen(true);
		},
		[],
	);

	const handleStatusConfirm = useCallback(async () => {
		if (!pendingStatusChange) return;

		const { config, newStatus } = pendingStatusChange;

		try {
			const response = await fetch(
				`/admin-portal/mitra-blast-configs/${config.id}`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						is_active: newStatus,
					}),
				},
			);

			const data = await response.json();

			if (data.success) {
				toast({
					title: t("toast.update_success"),
					description: data.message,
				});

				// Refresh the page data to show updated status
				router.reload({
					only: ["configs", "adminPortal", "flash", "errors", "formOptions"],
				});
			} else {
				throw new Error(
					data.errors?.join(", ") || data.message || "Failed to update status",
				);
			}
		} catch (error) {
			console.error("Failed to update status:", error);
			toast({
				title: t("toast.update_error"),
				description:
					error instanceof Error ? error.message : t("toast.please_try_again"),
				variant: "destructive",
			});
		}

		setStatusDialogOpen(false);
		setPendingStatusChange(null);
	}, [pendingStatusChange, toast, t]);

	const handleStatusCancel = useCallback(() => {
		setStatusDialogOpen(false);
		setPendingStatusChange(null);
	}, []);

	const columns = useMemo<ColumnDef<MitraBlastConfig>[]>(
		() => [
			{
				accessorKey: "locationCity",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.city")}
					/>
				),
				cell: ({ row }) => (
					<div className="text-nowrap">
						<span className="font-medium">{row.original.locationCity}</span>
					</div>
				),
				enableSorting: false,
				enableHiding: false,
			},
			{
				accessorKey: "serviceName",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.service")}
					/>
				),
				cell: ({ row }) => (
					<div className="text-nowrap">
						<span className="font-medium">
							{row?.original?.serviceName?.replaceAll("_", " ")}
						</span>
					</div>
				),
				enableSorting: false,
				enableHiding: false,
			},
			{
				accessorKey: "telegramGroupId",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.telegram_group_id")}
					/>
				),
				enableSorting: false,
				cell: ({ row }) => {
					const telegramId = row.original.telegramGroupId;
					const handleCopy = () => {
						if (telegramId) {
							copyToClipboard(telegramId);
							toast({
								title: "Copied!",
								description: "Telegram Group ID copied to clipboard",
							});
						}
					};
					return (
						<div className="flex items-center gap-2 text-nowrap">
							<span>{telegramId || "N/A"}</span>
							{telegramId && (
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6 opacity-60 hover:opacity-100"
									onClick={handleCopy}
									title="Copy"
								>
									<Copy className="h-3 w-3" />
								</Button>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "whatsappGroupId",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.whatsapp_group_id")}
					/>
				),
				enableSorting: false,
				cell: ({ row }) => {
					const whatsappId = row.original.whatsappGroupId;
					const handleCopy = () => {
						if (whatsappId) {
							copyToClipboard(whatsappId);
							toast({
								title: "Copied!",
								description: "WhatsApp Group ID copied to clipboard",
							});
						}
					};
					return (
						<div className="flex items-center gap-2 text-nowrap">
							<span>{whatsappId || "N/A"}</span>
							{whatsappId && (
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6 opacity-60 hover:opacity-100"
									onClick={handleCopy}
									title="Copy"
								>
									<Copy className="h-3 w-3" />
								</Button>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "isActive",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.status")}
					/>
				),
				enableSorting: false,
				cell: ({ row }) => (
					<div className="flex items-center gap-2 text-nowrap">
						<div className="inline-flex items-center gap-2">
							<Switch
								aria-label="Toggle switch"
								checked={row.original.isActive}
								id={`status-switch-${row.original.id}`}
								onCheckedChange={(checked) => {
									handleStatusToggle(row.original, checked);
								}}
							/>
							<Label
								className="font-medium text-sm"
								htmlFor={`status-switch-${row.original.id}`}
							>
								{row.original.isActive ? "Active" : "Inactive"}
							</Label>
						</div>
					</div>
				),
			},
			{
				accessorKey: "notes",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.notes")}
					/>
				),
				enableSorting: false,
				cell: ({ row }) => (
					<div className="text-nowrap text-muted-foreground/75">
						<span>{row.original.notes || "N/A"}</span>
					</div>
				),
			},
			{
				accessorKey: "createdAt",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.created_at")}
					/>
				),
				cell: ({ row }) => {
					const { date, time } = formatDateTime(
						row.original.createdAt,
						currentLocale,
					);
					return (
						<div className="text-nowrap text-muted-foreground/75">
							<p>{date}</p>
							<p>{time}</p>
						</div>
					);
				},
			},
			{
				accessorKey: "updatedAt",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={t("table.header.updated_at")}
					/>
				),
				cell: ({ row }) => {
					const { date, time } = formatDateTime(
						row.original.updatedAt,
						currentLocale,
					);
					return (
						<div className="text-nowrap text-muted-foreground/75">
							<p>{date}</p>
							<p>{time}</p>
						</div>
					);
				},
			},
			{
				id: "actions",
				cell: ({ row }) => (
					<div className="flex justify-end">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-muted-foreground"
								>
									<MoreHorizontal className="h-4 w-4" />
									<span className="sr-only">Open menu</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleEdit(row.original)}>
									<EditIcon className="h-4 w-4" />
									<span>{t("button.update_data")}</span>
								</DropdownMenuItem>
								<DropdownMenuItem
									className="text-destructive focus:text-destructive"
									onClick={() => handleDelete(row.original)}
								>
									<Trash2 className="h-4 w-4" />
									<span>{t("button.delete_data")}</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				),
			},
		],
		[t, toast, handleStatusToggle, handleEdit, handleDelete, currentLocale],
	);

	return (
		<>
			<Head title={t("head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("page_title")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<div className="z-10 flex gap-2 justify-between">
					<div className="flex items-center gap-2">
						<Button
							type="button"
							variant={isShowFilter ? "default" : "outline"}
							className="shadow-none"
							onClick={(event) => {
								event.preventDefault();
								handleToggleFilter();
							}}
						>
							<ListFilterIcon />
							{isShowFilter ? t("button.close_filter") : t("button.filter")}
						</Button>
					</div>

					<div className="flex items-center gap-2">
						<Button
							type="button"
							className="shadow-none"
							onClick={() => setAddDialogOpen(true)}
						>
							<PlusIcon />
							{t("button.add_data")}
						</Button>
					</div>
				</div>

				<AnimatePresence initial={false}>
					{isShowFilter && (
						<motion.section
							initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							transition={{ duration: 0.3, ease: "easeOut" }}
							className="p-3 border shadow-inner rounded-xl bg-background space-y-3"
						>
							<div className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
								<Deferred
									data={["locations"]}
									fallback={<Skeleton className="w-full rounded-md h-10" />}
								>
									<Popover
										open={cityPopoverOpen}
										onOpenChange={setCityPopoverOpen}
									>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												className={cn(
													"w-full justify-between bg-input shadow-inner h-full p-2",
													!currentCity && "font-normal text-muted-foreground",
												)}
											>
												<p className="truncate">
													{currentCity ? currentCity : t("filter.city")}
												</p>
												<ChevronsUpDown className="opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
											<Command>
												<CommandInput
													className="h-9"
													autoComplete="address-level2"
													placeholder={t("filter.city")}
												/>
												<CommandList>
													<CommandEmpty>{t("filter.all_cities")}</CommandEmpty>
													{groupedLocations?.map((location) => (
														<Fragment key={location.country}>
															<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
																{location.country}
															</span>

															{location.states.map((state) => (
																<CommandGroup
																	key={state.name}
																	heading={state.name}
																>
																	{state.cities.map((city) => (
																		<CommandItem
																			key={city.name}
																			value={city.name}
																			onSelect={() => {
																				handleCityChange(city.name);
																			}}
																			className="justify-between"
																		>
																			<span>{city.name}</span>
																			<Check
																				className={cn(
																					"ml-auto",
																					city.name === currentCity
																						? "opacity-100"
																						: "opacity-0",
																				)}
																			/>
																		</CommandItem>
																	))}
																</CommandGroup>
															))}
														</Fragment>
													))}
												</CommandList>

												<CommandList>
													<CommandGroup>
														<CommandSeparator className="mb-2" />
														<CommandItem
															value="all"
															onSelect={() => {
																handleCityChange("all");
															}}
															className="uppercase justify-between"
														>
															<span className="mx-auto font-medium text-center uppercase w-fit">
																{t("filter.all_cities")}
															</span>
														</CommandItem>
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
								</Deferred>

								<Popover
									open={statusPopoverOpen}
									onOpenChange={setStatusPopoverOpen}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"w-full justify-between bg-input shadow-inner h-full p-2",
												!currentStatus && "font-normal text-muted-foreground",
											)}
										>
											<p className="truncate">
												{currentStatus
													? t(`filter.status_options.${currentStatus}`)
													: t("filter.status")}
											</p>
											<ChevronsUpDown className="opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
										<Command>
											<CommandInput
												className="h-9"
												placeholder={t("filter.status")}
											/>
											<CommandList>
												<CommandEmpty>{t("filter.all_statuses")}</CommandEmpty>
												<CommandGroup>
													<CommandItem
														value="active"
														onSelect={() => {
															handleStatusChange("active");
														}}
														className="justify-between"
													>
														<DotBadgeWithLabel
															size="xs"
															className="relative flex-shrink-0 text-left"
															variant="success"
														>
															<span
																title="Active"
																className="flex-grow-0 text-xs uppercase tracking-wide text-nowrap"
															>
																{t("filter.status_options.active")}
															</span>
														</DotBadgeWithLabel>
														<Check
															className={cn(
																"ml-auto",
																currentStatus === "active"
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
													</CommandItem>
													<CommandItem
														value="inactive"
														onSelect={() => {
															handleStatusChange("inactive");
														}}
														className="justify-between"
													>
														<DotBadgeWithLabel
															size="xs"
															className="relative flex-shrink-0 text-left"
															variant="destructive"
														>
															<span
																title="Inactive"
																className="flex-grow-0 text-xs uppercase tracking-wide text-nowrap"
															>
																{t("filter.status_options.inactive")}
															</span>
														</DotBadgeWithLabel>
														<Check
															className={cn(
																"ml-auto",
																currentStatus === "inactive"
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
													</CommandItem>
												</CommandGroup>
											</CommandList>

											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														value="all"
														onSelect={() => {
															handleStatusChange("all");
														}}
														className="uppercase justify-between"
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{t("filter.all_statuses")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>

								<Popover
									open={servicePopoverOpen}
									onOpenChange={setServicePopoverOpen}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"w-full justify-between bg-input shadow-inner h-full p-2",
												!currentService && "font-normal text-muted-foreground",
											)}
										>
											<p className="truncate">
												{currentService
													? formOptions?.services
															?.find((s) => String(s.id) === currentService)
															?.name?.replaceAll("_", " ") || currentService
													: t("filter.service")}
											</p>
											<ChevronsUpDown className="opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
										<Command>
											<CommandInput
												className="h-9"
												placeholder={t("filter.service")}
											/>
											<CommandList>
												<CommandEmpty>{t("filter.all_services")}</CommandEmpty>
												<CommandGroup>
													{formOptions?.services?.map((service) => (
														<CommandItem
															key={service.id}
															value={service.name}
															onSelect={() => {
																handleServiceChange(String(service.id));
															}}
															className="justify-between"
														>
															<span>{service.name.replaceAll("_", " ")}</span>
															<Check
																className={cn(
																	"ml-auto",
																	String(service.id) === currentService
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>

											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														value="all"
														onSelect={() => {
															handleServiceChange("all");
														}}
														className="uppercase justify-between"
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{t("filter.all_services")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</div>

							<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 col-span-full md:flex-row">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="w-full md:w-fit"
									onClick={(event) => {
										event.preventDefault();
										handleToggleFilter();
									}}
								>
									{t("button.close_filter")}
								</Button>

								<Button
									type="button"
									size="sm"
									variant="ghost-destructive"
									className="w-full md:w-fit"
									disabled={!currentCity && !currentStatus && !currentService}
									onClick={(event) => {
										event.preventDefault();
										handleClearAll();
									}}
								>
									{t("button.clear_all_filters")}
								</Button>
							</div>
						</motion.section>
					)}
				</AnimatePresence>

				<Deferred
					data={["configs"]}
					fallback={
						<div className="space-y-4">
							<Skeleton className="w-2/12 h-4 rounded-sm" />
							<Skeleton className="relative w-full h-32 rounded-xl" />
						</div>
					}
				>
					<div className="space-y-4 grid grid-cols-1">
						<DataTable
							columns={columns}
							data={configsData}
							customPagination={() =>
								configs?.metadata ? (
									<ApptPagination
										label={t("pagination.mitra_blast_configs")}
										metadata={configs?.metadata}
										actions={{
											goToPrevpage: () => changePage("prev"),
											goToNextPage: () => changePage("next"),
											onChangeLimit: (value) => changePage("limit", value),
											goToPage: (value) => changePage("page", value),
											goToFirstPage: () => changePage("first"),
											goToLastPage: () => changePage("last"),
										}}
									/>
								) : null
							}
						/>
					</div>
				</Deferred>

				<AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{pendingStatusChange?.newStatus
									? t("modal.status.activate_title")
									: t("modal.status.deactivate_title")}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{pendingStatusChange?.newStatus ? (
									<span className="text-pretty">
										{t("modal.status.activate_description", {
											city: pendingStatusChange?.config?.locationCity,
										})}
									</span>
								) : (
									<span className="text-pretty">
										{t("modal.status.deactivate_description", {
											city: pendingStatusChange?.config?.locationCity,
										})}
									</span>
								)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={handleStatusCancel}>
								{t("button.cancel")}
							</AlertDialogCancel>

							<AlertDialogAction onClick={handleStatusConfirm}>
								{pendingStatusChange?.newStatus
									? t("button.activate")
									: t("button.deactivate")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t("modal.delete.title")}</AlertDialogTitle>
							<AlertDialogDescription>
								<span className="text-pretty">
									{t("modal.delete.description", {
										city: pendingDeleteConfig?.locationCity,
									})}
								</span>
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={handleDeleteCancel}>
								{t("button.cancel")}
							</AlertDialogCancel>

							<AlertDialogAction
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								onClick={handleDeleteConfirm}
							>
								{t("button.delete")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<ResponsiveDialog
					isOpen={addDialogOpen}
					onOpenChange={setAddDialogOpen}
					title={t("modal.add.title")}
					description={t("modal.add.description")}
				>
					<MitraBlastConfigForm
						locations={formOptions?.locations || []}
						locationServices={formOptions?.locationServices || []}
						onSuccess={() => {
							setAddDialogOpen(false);
							router.reload({
								only: [
									"configs",
									"formOptions",
									"adminPortal",
									"flash",
									"errors",
								],
							});
						}}
					/>
				</ResponsiveDialog>

				<ResponsiveDialog
					isOpen={editDialogOpen}
					onOpenChange={setEditDialogOpen}
					title={t("modal.update.title")}
					description={t("modal.update.description")}
				>
					{selectedConfig && (
						<MitraBlastConfigForm
							locations={locations || []}
							locationServices={formOptions?.locationServices || []}
							initialData={selectedConfig}
							onSuccess={() => {
								setEditDialogOpen(false);
								router.reload({
									only: [
										"configs",
										"formOptions",
										"adminPortal",
										"flash",
										"errors",
									],
								});
							}}
						/>
					)}
				</ResponsiveDialog>
			</PageContainer>
		</>
	);
}
